import {
  Autocomplete,
  Box,
  Button,
  Card,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import EditIcon from '@mui/icons-material/Edit'
import DeleteIcon from '@mui/icons-material/Delete'
import PreviewIcon from '@mui/icons-material/PlayCircleOutline'
import { useEffect, useState } from 'react'
import { activityTemplateService } from '../../../../services/activityTemplateService'
import { useAuthContext } from '../../../../hooks/useAuthContext'
import adminDocumentTemplateService from '../../../../services/admin/document'
import { toast } from 'react-toastify'
import ConfirmationBox from '../../../../components/Dashboard/ConfirmationBox'
import { fieldTemplateService } from '../../../../services/fieldTemplateService'
import { ACTIVITY_TYPES } from '../../../../utility/constants'
import { sanitizeHtml } from '../../../../utility/utilityFunctions'
import DocumentTemplateFormModal from './DocumentTemplateFormModal'

const INITIAL_MODAL_DATA = {
  id: 0,
  document_template_name: '',
  activity_template_id: null,
  html_template: '',
  sequence: 1,
  repeater_table_field_template_id: null,
  grouping_table_config: [],
  is_landscape: false,
  is_draft_until_end_accepted: false,
  activities_for_approval_grid: [],
  trigger_route_template_ids: [],
}

const normalizeGroupingConfig = (config) => {
  if (!config) return null

  let groupingHeaders = []
  if (Array.isArray(config.grouping_headers)) {
    groupingHeaders = config.grouping_headers.map((h) => {
      if (typeof h === 'object' && h !== null) {
        return {
          field_id: h.field_id,
          header: h.header || { display_pattern: '', bg_color: '#f8fafc' },
          total: h.total || {
            visible: true,
            bg_color: '#f1f5f9',
            display_pattern: '',
            subtotal_fields: []
          }
        }
      }
      return {
        field_id: h,
        header: { display_pattern: '', bg_color: '#f8fafc' },
        total: {
          visible: true,
          bg_color: '#f1f5f9',
          display_pattern: '',
          subtotal_fields: []
        }
      }
    })
  }

  return {
    ...config,
    grouping_headers: groupingHeaders
  }
}

const DocumentTemplates = () => {
  const { user } = useAuthContext()

  const [loading, setLoading] = useState(false)
  const [buttonLoading, setButtonLoading] = useState(false)

  // Form drawer state
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [modalData, setModalData] = useState(INITIAL_MODAL_DATA)

  // Data
  const [allTemplates, setAllTemplates] = useState([])
  const [allActivities, setAllActivities] = useState([])
  const [approvalGridActivities, setApprovalGridActivities] = useState([])
  const [allRoutes, setAllRoutes] = useState(null)  // null = not yet loaded
  const [allTables, setAllTables] = useState([])
  const [activityFields, setActivityFields] = useState([])

  // Delete template confirmation
  const [openDeleteTemplate, setOpenDeleteTemplate] = useState(false)
  const [deleteTemplateDetails, setDeleteTemplateDetails] = useState({ name: '', id: null })

  // Live preview
  const [livePreview, setLivePreview] = useState({
    open: false,
    template: null,
    processList: [],
    loadingList: false,
    selectedProcess: null,
    renderedHtml: null,
    loadingHtml: false,
    currentPage: 0,
    pdfUrl: null,
  })

  // ── Initial load ─────────────────────────────────────────────────────────
  useEffect(() => {
    if (!user) return
    ;(async () => {
  try {
    setLoading(true)

    const [templates, activities] = await Promise.all([
      adminDocumentTemplateService.getDocumentTemplatesOfCompany(user, user.company_id),
      activityTemplateService.getAllActivitiesOfCompany(user, user.company_id),
    ])

    setAllTemplates(templates)
    setAllActivities(activities)
  } catch (err) {
    toast.error('Failed to load templates')
  } finally {
    setLoading(false)
  }
})()
  }, [user])

  // ── Load routes + tables when activity changes ───────────────────────────
  useEffect(() => {
    if (!drawerOpen || !modalData.activity_template_id) {
      setAllRoutes(null)
      return
    }
    ;(async () => {
      const selectedActivity = allActivities.find(
        (it) => it.activity_template_id == modalData.activity_template_id,
      )
      const DEActivityTemplateId =
        selectedActivity?.activity_type === ACTIVITY_TYPES.REVIEW
          ? selectedActivity?.associated_data_entry_activity_template_id
          : modalData.activity_template_id

      const [routesRes, tablesRes, fieldsRes] = await Promise.all([
        activityTemplateService.getAllToRoutes(user, modalData.activity_template_id),
        fieldTemplateService.getTableSubfields(user, DEActivityTemplateId),
        fieldTemplateService.getActivityFieldsForAdmin(user, DEActivityTemplateId),
      ])

      setAllRoutes(routesRes.data)
      setAllTables(tablesRes.data)
      setActivityFields(Array.isArray(fieldsRes) ? fieldsRes : [])

      if (!modalData.trigger_route_template_ids?.length) {
        setModalData((prev) => ({
          ...prev,
          trigger_route_template_ids: routesRes.data?.map((it) => it.route_template_id) || [],
        }))
      }

      const processTemplateId = allActivities.find(
        (it) => it.activity_template_id == modalData.activity_template_id,
      )?.process_template_id

      if (processTemplateId) {
        setApprovalGridActivities(
          allActivities.filter((it) => it.process_template_id == processTemplateId),
        )
      }
    })()
  }, [user, drawerOpen, modalData.activity_template_id])

  // ── Drawer open / close ──────────────────────────────────────────────────
  const handleOpenDrawer = (data = INITIAL_MODAL_DATA) => {
    const normalizedConfigs = (data?.grouping_table_config || []).map((cfg) => normalizeGroupingConfig(cfg))
    setModalData({
      ...data,
      grouping_table_config: normalizedConfigs
    })
    setDrawerOpen(true)
  }

  const handleCloseDrawer = () => {
    setDrawerOpen(false)
    setModalData(INITIAL_MODAL_DATA)
  }

  // ── Save ─────────────────────────────────────────────────────────────────
  const handleCreateOrUpdateTemplate = async () => {
    if (
      !modalData.document_template_name?.length ||
      !modalData.activity_template_id ||
      !modalData.html_template?.length
    ) {
      return toast.error('Please fill the required fields')
    }
    setButtonLoading(true)
    try {
      const sanitizedHtml = sanitizeHtml(modalData.html_template)
      const payload = { ...modalData, html_template: sanitizedHtml }

      if (payload.id) {
        await adminDocumentTemplateService.updateDocumentTemplate(user, payload.id, payload)
        setAllTemplates((prev) => prev.map((t) => (t.id == payload.id ? payload : t)))
        toast.success('Template updated successfully!')
      } else {
        const result = await adminDocumentTemplateService.saveDocumentTemplate(user, payload)
        setAllTemplates((prev) => [{ ...payload, id: result }, ...prev])
        toast.success('Template created successfully!')
      }
      handleCloseDrawer()
    } catch {
      toast.error('Something went wrong')
    } finally {
      setButtonLoading(false)
    }
  }

  // ── Delete template ──────────────────────────────────────────────────────
  const handleDeleteTemplate = async () => {
    try {
      await adminDocumentTemplateService.deleteDocumentTemplate(user, deleteTemplateDetails.id)
      setAllTemplates((prev) => prev.filter((t) => t.id != deleteTemplateDetails.id))
      toast.success('Template deleted successfully!')
      setOpenDeleteTemplate(false)
      setDeleteTemplateDetails({ name: '', id: null })
    } catch {
      toast.error('Something went wrong')
    }
  }

  // ── Live preview ─────────────────────────────────────────────────────────
  const openLivePreview = async (template) => {
    setLivePreview((p) => ({
      ...p,
      open: true,
      template,
      loadingList: true,
      processList: [],
      selectedProcess: null,
      renderedHtml: null,
      currentPage: 0,
      pdfUrl: null,
    }))
    try {
      const list = await adminDocumentTemplateService.getProcessDataForDocPreview(user, template.activity_template_id)
      setLivePreview((p) => ({ ...p, processList: list, loadingList: false }))
    } catch {
      toast.error('Failed to load process list')
      setLivePreview((p) => ({ ...p, loadingList: false }))
    }
  }

  const closeLivePreview = () => {
    if (livePreview.pdfUrl) {
      window.URL.revokeObjectURL(livePreview.pdfUrl)
    }
    setLivePreview({
      open: false,
      template: null,
      processList: [],
      loadingList: false,
      selectedProcess: null,
      renderedHtml: null,
      loadingHtml: false,
      currentPage: 0,
      pdfUrl: null,
    })
  }

  const fetchRenderedHtml = async (processRecord) => {
    if (!processRecord || !livePreview.template) return
    setLivePreview((p) => {
      if (p.pdfUrl) window.URL.revokeObjectURL(p.pdfUrl)
      return { ...p, loadingHtml: true, renderedHtml: null, pdfUrl: null }
    })
    try {
      const response = await adminDocumentTemplateService.downloadPreviewPdf(
        user,
        processRecord.id,
        livePreview.template.id,
        null
      )
      const url = window.URL.createObjectURL(new Blob([response], { type: 'application/pdf' }))
      setLivePreview((p) => ({ ...p, pdfUrl: url, loadingHtml: false }))
    } catch {
      toast.error('Failed to render preview PDF')
      setLivePreview((p) => ({ ...p, loadingHtml: false }))
    }
  }



  // ── Render ───────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <Box sx={{ height: '80vh', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <CircularProgress color="secondary" size={40} />
      </Box>
    )
  }

  return (
    <Box mt={2}>
      {/* Page header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" fontWeight="bold">
          Document Templates
        </Typography>
        <Button color="secondary" variant="contained" onClick={() => handleOpenDrawer()}>
          Create New Template
        </Button>
      </Box>

      {/* Template list */}
      <Box maxHeight="65vh" overflow="scroll">
        {allTemplates.length > 0 ? (
          allTemplates.map((template) => (
            <Card elevation={1} sx={{ px: 2, py: 1.5, mb: 1 }} key={template.id}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="body1" fontWeight={500}>
                  <Typography component="span" variant="body2" color="text.secondary" mr={0.5}>
                    #{template.id}
                  </Typography>
                  {template.document_template_name}
                </Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Tooltip title="Edit Template">
                    <IconButton
                      onClick={() => handleOpenDrawer(template)}
                      sx={{ bgcolor: '#f2eeed', height: 32, width: 32 }}
                      size="small"
                    >
                      <EditIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Preview with live data">
                    <IconButton
                      onClick={() => openLivePreview(template)}
                      sx={{ bgcolor: '#edf7ed', height: 32, width: 32 }}
                      size="small"
                    >
                      <PreviewIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Delete Template">
                    <IconButton
                      onClick={() => {
                        setOpenDeleteTemplate(true)
                        setDeleteTemplateDetails({ name: template.document_template_name, id: template.id })
                      }}
                      sx={{ bgcolor: '#f2eeed', height: 32, width: 32 }}
                      size="small"
                    >
                      <DeleteIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>
            </Card>
          ))
        ) : (
          <Typography color="text.secondary" variant="body2">
            No templates yet.
          </Typography>
        )}
      </Box>

      {/* Form Drawer */}
      <DocumentTemplateFormModal
        open={drawerOpen}
        onClose={handleCloseDrawer}
        modalData={modalData}
        setModalData={setModalData}
        allActivities={allActivities}
        allTables={allTables}
        allRoutes={allRoutes}
        activityFields={activityFields}
        approvalGridActivities={approvalGridActivities}
        buttonLoading={buttonLoading}
        onSave={handleCreateOrUpdateTemplate}
      />

      {/* Delete Template Confirmation */}
      <ConfirmationBox
        isDialogOpen={openDeleteTemplate}
        setIsDialogOpen={setOpenDeleteTemplate}
        title={`Are you sure you want to delete "${deleteTemplateDetails?.name}"?`}
        onYes={handleDeleteTemplate}
      />

      {/* Live Data Preview Dialog */}
      <Dialog
        open={livePreview.open}
        fullWidth
        maxWidth="xl"
        onClose={closeLivePreview}
        PaperProps={{ sx: { height: '90vh', display: 'flex', flexDirection: 'column' } }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          Preview: <b>{livePreview.template?.document_template_name}</b>
        </DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, overflow: 'hidden', flex: 1 }}>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexShrink: 0 }}>
            <Autocomplete
              size="small"
              sx={{ flex: 1 }}
              options={livePreview.processList}
              loading={livePreview.loadingList}
              getOptionLabel={(opt) =>
                `${opt.process_reference_num}${opt.version > 1 ? ` (v${opt.version})` : ''}`
              }
              isOptionEqualToValue={(o, v) => o.id === v.id}
              value={livePreview.selectedProcess}
              onChange={(_, val) => setLivePreview((p) => ({ ...p, selectedProcess: val }))}
              renderInput={(params) => (
                <TextField {...params} color="secondary" label="Select process reference number" />
              )}
            />
            <Button
              variant="contained"
              color="secondary"
              disabled={!livePreview.selectedProcess || livePreview.loadingHtml}
              onClick={() => fetchRenderedHtml(livePreview.selectedProcess)}
              startIcon={livePreview.loadingHtml ? <CircularProgress size={16} color="inherit" /> : <PreviewIcon />}
            >
              {livePreview.loadingHtml ? 'Generating...' : 'Generate Preview'}
            </Button>

          </Box>

          <Box sx={{ flex: 1, overflow: 'hidden', border: '1px solid #e0e0e0', borderRadius: 1, bgcolor: '#fff', p: 0, height: '100%' }}>
            {livePreview.loadingHtml && (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                <CircularProgress color="secondary" />
              </Box>
            )}
            {!livePreview.loadingHtml && livePreview.pdfUrl && (
              <iframe
                src={livePreview.pdfUrl}
                title="PDF Preview"
                width="100%"
                height="100%"
                style={{ border: 'none', display: 'block' }}
              />
            )}
            {!livePreview.loadingHtml && !livePreview.pdfUrl && (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', color: 'text.secondary', p: 2 }}>
                <Typography variant="body2">Select a process reference and click "Generate Preview"</Typography>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" color="info" onClick={closeLivePreview}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default DocumentTemplates